/**
 * Guides Page - Shopping Tips & Coupon Guide
 *
 * Coupang and AliExpress shopping guides
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Tag,
  Truck,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Calendar,
  Percent,
  Gift,
  Star,
  Clock,
  Globe,
  Shield
} from 'lucide-react';

interface GuideSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

export function GuidesPage() {
  const [openSection, setOpenSection] = useState<string>('coupang-basics');

  const toggleSection = (id: string) => {
    setOpenSection(openSection === id ? '' : id);
  };

  return (
    <div className="container py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          쇼핑 가이드
        </h1>
        <p className="text-gray-600">
          쿠팡과 알리익스프레스에서 스마트하게 쇼핑하는 방법을 알아보세요.
        </p>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Link
          to="#coupang-basics"
          onClick={() => setOpenSection('coupang-basics')}
          className="p-4 bg-blue-50 rounded-xl border border-blue-200 hover:bg-blue-100 transition-colors"
        >
          <div className="text-blue-600 mb-2">🛒</div>
          <div className="text-sm font-medium text-blue-900">쿠팡 기초</div>
        </Link>
        <Link
          to="#aliexpress-basics"
          onClick={() => setOpenSection('aliexpress-basics')}
          className="p-4 bg-orange-50 rounded-xl border border-orange-200 hover:bg-orange-100 transition-colors"
        >
          <div className="text-orange-600 mb-2">🛍️</div>
          <div className="text-sm font-medium text-orange-900">알리 기초</div>
        </Link>
        <Link
          to="#aliexpress-coupons"
          onClick={() => setOpenSection('aliexpress-coupons')}
          className="p-4 bg-red-50 rounded-xl border border-red-200 hover:bg-red-100 transition-colors"
        >
          <div className="text-red-600 mb-2">🎫</div>
          <div className="text-sm font-medium text-red-900">쿠폰 활용</div>
        </Link>
        <Link
          to="#customs"
          onClick={() => setOpenSection('customs')}
          className="p-4 bg-green-50 rounded-xl border border-green-200 hover:bg-green-100 transition-colors"
        >
          <div className="text-green-600 mb-2">📦</div>
          <div className="text-sm font-medium text-green-900">관부가세</div>
        </Link>
      </div>

      {/* Guide Sections */}
      <div className="space-y-4">
        {/* Coupang Basics */}
        <GuideSection
          id="coupang-basics"
          isOpen={openSection === 'coupang-basics'}
          onToggle={toggleSection}
          icon={<span className="text-2xl">🛒</span>}
          title="쿠팡 쇼핑 잘하는 법"
          badge="기초"
          badgeColor="blue"
        >
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Truck size={18} className="text-blue-500" />
                로켓배송 활용법
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>로켓배송</strong> 상품은 무료배송 + 익일 도착 (오전 주문 시)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>로켓프레시</strong>는 신선식품 전용, 새벽 배송 가능</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>로켓직구</strong>는 해외직구 전용, 관부가세 포함 가격 표시</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CreditCard size={18} className="text-blue-500" />
                결제 팁
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>쿠페이</strong> 결제 시 추가 적립 혜택</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>제휴 카드(신한, KB 등) 사용 시 추가 할인</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>첫 구매 시 <strong>웰컴 쿠폰</strong> 필수 사용 (최대 1만원 할인)</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Star size={18} className="text-blue-500" />
                리뷰 확인 요령
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>포토리뷰</strong> 위주로 확인 (실제 상품 확인 가능)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>최근 1개월 리뷰 집중 확인 (품질 변화 가능성)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>별점 4.5 이상 + 리뷰 100개 이상 상품 추천</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">💡 프로 팁</h4>
              <p className="text-sm text-blue-800">
                매일 오전 7시, 오후 4시에 <strong>타임특가</strong> 상품 업데이트!
                인기 상품을 최대 70%까지 할인받을 수 있습니다.
              </p>
            </div>
          </div>
        </GuideSection>

        {/* AliExpress Basics */}
        <GuideSection
          id="aliexpress-basics"
          isOpen={openSection === 'aliexpress-basics'}
          onToggle={toggleSection}
          icon={<span className="text-2xl">🛍️</span>}
          title="알리익스프레스 기초 가이드"
          badge="기초"
          badgeColor="orange"
        >
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Globe size={18} className="text-orange-500" />
                한국 배송 가능 여부 확인
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>상품 페이지에서 <strong>"South Korea"</strong> 배송 옵션 확인</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>AliExpress Standard Shipping</strong> 추천 (추적 가능)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>배송 기간: 보통 <strong>2-4주</strong> 소요</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Shield size={18} className="text-orange-500" />
                안전한 구매를 위한 팁
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>피드백 점수 95% 이상</strong> 판매자 선택</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>주문 수 1,000개 이상</strong> 인기 상품 위주 구매</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>항상 <strong>사진/동영상</strong>으로 상태 확인</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>판매자와 <strong>채팅</strong>으로 재고/배송 확인</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Clock size={18} className="text-orange-500" />
                대형 세일 일정
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-orange-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-orange-900">3월</div>
                  <div className="text-xs text-orange-700">Anniversary Sale</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-orange-900">6월</div>
                  <div className="text-xs text-orange-700">618 Mid-Year Sale</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-orange-900">11월</div>
                  <div className="text-xs text-orange-700">11.11 싱글데이</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-orange-900">11월 말</div>
                  <div className="text-xs text-orange-700">블랙프라이데이</div>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <h4 className="font-semibold text-orange-900 mb-2">⚠️ 주의사항</h4>
              <ul className="text-sm text-orange-800 space-y-1">
                <li>• 전자제품은 한국 A/S 불가능할 수 있음</li>
                <li>• 충전기/어댑터는 한국 규격(220V) 확인 필수</li>
                <li>• 배송 지연 시 판매자에게 문의 (자동 환불 방지)</li>
              </ul>
            </div>
          </div>
        </GuideSection>

        {/* AliExpress Coupons */}
        <GuideSection
          id="aliexpress-coupons"
          isOpen={openSection === 'aliexpress-coupons'}
          onToggle={toggleSection}
          icon={<span className="text-2xl">🎫</span>}
          title="알리익스프레스 쿠폰 & 할인 완벽 가이드"
          badge="필수"
          badgeColor="red"
        >
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Gift size={18} className="text-red-500" />
                신규 회원 혜택
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Welcome Deal</strong>: 첫 구매 최대 $50 할인</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>$3 쿠폰</strong>: $5 이상 구매 시 사용 가능</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>신규 회원 전용 특가 상품 접근 가능</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Tag size={18} className="text-red-500" />
                쿠폰 종류와 사용법
              </h4>
              <div className="space-y-3">
                <div className="border rounded-lg p-3">
                  <div className="font-medium text-gray-900">🛒 장바구니 쿠폰</div>
                  <div className="text-sm text-gray-600 mt-1">
                    장바구니 페이지에서 수령, 결제 시 자동 적용
                  </div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="font-medium text-gray-900">🏪 스토어 쿠폰</div>
                  <div className="text-sm text-gray-600 mt-1">
                    특정 판매자 상품에만 사용 가능, 스토어 페이지에서 수령
                  </div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="font-medium text-gray-900">🎯 셀렉트 쿠폰</div>
                  <div className="text-sm text-gray-600 mt-1">
                    특정 카테고리/상품에 적용, 한정 수량
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Percent size={18} className="text-red-500" />
                쿠폰 중복 사용 전략
              </h4>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm text-green-800">
                  <strong>최대 할인 공식:</strong>
                  <div className="mt-2 p-3 bg-white rounded border">
                    <code>세일가 × 스토어쿠폰 × 장바구니쿠폰 × 결제혜택</code>
                  </div>
                  <div className="mt-3">
                    예: $100 상품 → 세일 20% → $80 → 스토어쿠폰 $5 → $75 → 장바구니쿠폰 $10 → <strong>$65</strong>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Calendar size={18} className="text-red-500" />
                2025년 이달의 쿠폰 일정
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">월</th>
                      <th className="px-3 py-2 text-left">주요 이벤트</th>
                      <th className="px-3 py-2 text-left">예상 할인</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="px-3 py-2">2월</td>
                      <td className="px-3 py-2">설날 세일</td>
                      <td className="px-3 py-2 text-red-600">최대 70%</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">3월</td>
                      <td className="px-3 py-2">Anniversary Sale</td>
                      <td className="px-3 py-2 text-red-600">최대 50%</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">6월</td>
                      <td className="px-3 py-2">618 Super Sale</td>
                      <td className="px-3 py-2 text-red-600">최대 70%</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">11월</td>
                      <td className="px-3 py-2">11.11 Global Sale</td>
                      <td className="px-3 py-2 text-red-600">최대 80%</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">12월</td>
                      <td className="px-3 py-2">연말 세일</td>
                      <td className="px-3 py-2 text-red-600">최대 60%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-red-50 rounded-lg p-4">
              <h4 className="font-semibold text-red-900 mb-2">🔥 쿠폰 수령 꿀팁</h4>
              <ul className="text-sm text-red-800 space-y-1">
                <li>• 매일 오전 10시(한국시간) 새 쿠폰 업데이트</li>
                <li>• 앱 전용 쿠폰이 더 많음 - 앱 설치 권장</li>
                <li>• "Get Coupons" 버튼 놓치지 말고 클릭!</li>
                <li>• 장바구니에 담아두면 할인 알림 받을 수 있음</li>
              </ul>
            </div>
          </div>
        </GuideSection>

        {/* Customs & Taxes */}
        <GuideSection
          id="customs"
          isOpen={openSection === 'customs'}
          onToggle={toggleSection}
          icon={<span className="text-2xl">📦</span>}
          title="관부가세 완벽 가이드"
          badge="중요"
          badgeColor="green"
        >
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <AlertTriangle size={18} className="text-yellow-500" />
                관부가세 기준
              </h4>
              <div className="bg-yellow-50 rounded-lg p-4 mb-4">
                <div className="text-sm text-yellow-800">
                  <strong>2024년 기준 면세 한도:</strong>
                  <ul className="mt-2 space-y-1">
                    <li>• <strong>미국 발송:</strong> $200 달러까지 면세</li>
                    <li>• <strong>중국/기타:</strong> $150 달러까지 면세</li>
                    <li>• <strong>목록통관:</strong> $150-$200 사이는 목록통관 가능</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">관부가세 계산법</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-700">
                  <strong>계산 공식:</strong>
                  <div className="mt-2 p-3 bg-white rounded border">
                    <code>(물품가격 + 배송비 - 면세한도) × 관세율 + 부가세</code>
                  </div>
                  <div className="mt-3">
                    <strong>예시:</strong> $250 컴퓨터 부품 (중국)
                    <ul className="mt-1 ml-4 space-y-1">
                      <li>- 과세가격: $250 - $150 = $100</li>
                      <li>- 관세 (약 8%): $8</li>
                      <li>- 부가세 (10%): $10.8</li>
                      <li>- <strong>예상 관부가세: 약 $19 (≈ 28,000원)</strong></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-500" />
                관부가세 절약 팁
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>여러 상품을 한 번에 주문하지 말고 <strong>분할 주문</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>$150 미만</strong>으로 주문하면 100% 면세</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>세일 기간에 나눠서 구매 (가격이 낮아질수록 유리)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>관세청 <strong>예상 세액 조회</strong> 서비스 활용</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">📱 유용한 서비스</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>관세청 예상세액조회</strong>: unipass.customs.go.kr</li>
                <li>• <strong>나의 통관정보 조회</strong>: portal.customs.go.kr</li>
                <li>• <strong>관세평가원</strong>: 국가별 관세율 확인</li>
              </ul>
            </div>
          </div>
        </GuideSection>
      </div>

      {/* CTA */}
      <div className="mt-12 text-center">
        <Link
          to="/products"
          className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors"
        >
          지금 쇼핑하러 가기
          <ExternalLink size={18} />
        </Link>
      </div>
    </div>
  );
}

interface GuideSectionProps {
  id: string;
  isOpen: boolean;
  onToggle: (id: string) => void;
  icon: React.ReactNode;
  title: string;
  badge?: string;
  badgeColor?: 'blue' | 'orange' | 'red' | 'green';
  children: React.ReactNode;
}

function GuideSection({ id, isOpen, onToggle, icon, title, badge, badgeColor = 'blue', children }: GuideSectionProps) {
  const badgeColors = {
    blue: 'bg-blue-100 text-blue-700',
    orange: 'bg-orange-100 text-orange-700',
    red: 'bg-red-100 text-red-700',
    green: 'bg-green-100 text-green-700',
  };

  return (
    <div id={id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span>{icon}</span>
          <span className="font-semibold text-gray-900">{title}</span>
          {badge && (
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badgeColors[badgeColor]}`}>
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp size={20} className="text-gray-400" />
        ) : (
          <ChevronDown size={20} className="text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="pt-4">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

export default GuidesPage;
